import type { Editor } from '@tiptap/react';
import {
  BookOpen,
  Cloud,
  CloudOff,
  Download,
  FileText,
  GitCompareArrows,
  History,
  LockKeyhole,
  LockKeyholeOpen,
  Mountain,
  Redo2,
  Shield,
  Undo2,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';

import type { UpdateStatus } from '../shared/desktop';
import { blockText } from '../shared/export';
import { type FocusSession, focusChanges } from '../shared/focus';
import { resolveImages } from '../shared/media';
import {
  type Area,
  areas,
  type LocalRecord,
  type Note,
  type RecordData,
  type Task,
} from '../shared/model';
import { taskSuggestions } from '../shared/productivity';

import { AuthGate } from './components/AuthGate';
import { BackupStatus } from './components/BackupStatus';
import { CalendarPage, CalendarReminders } from './components/CalendarPage';
import {
  AboutCairn,
  CommandCenter,
  DesktopUpdateNotice,
  GoToPalette,
  HistoryButtons,
  useLatest,
} from './components/DesktopChrome';
import { DesktopDownload } from './components/DesktopDownload';
import { FocusActivity, focusRuntime } from './components/FocusHistory';
import { FocusPanel, MarkerStarts } from './components/FocusPanel';
import { LearningPage } from './components/LearningPage';
import { AppearancePicker, MorePage, NotesPage, Sidebar, TabBar } from './components/Navigation';
import { NoteEditor } from './components/NoteEditor';
import { NoteIconPicker } from './components/NoteIconPicker';
import { NoteList } from './components/NoteList';
import { GoalsPage, PeoplePage, ReviewsPage, TaskContext } from './components/Productivity';
import { DesktopReminders, ReminderDelivery } from './components/ReminderDelivery';
import { ConflictBanner, ConflictReview } from './components/SyncConflicts';
import { TaskPage } from './components/TaskPage';
import { TasksTable } from './components/TasksTable';
import { TodayPage } from './components/TodayPage';
import {
  ActionIconButton,
  Button,
  ButtonLink,
  Disclosure,
  IconButton,
  InlineCreate,
  Input,
  MobileDetails,
  Modal,
  PageHeader,
  SearchField,
  Select,
  Textarea,
  useMobileLayout,
} from './components/ui';
import { VaultLab } from './components/VaultLab';
import { WorkspaceSettings } from './components/WorkspaceSettings';
import { WorkspaceTransfer } from './components/WorkspaceTransfer';
import { WorkspaceTrash } from './components/WorkspaceTrash';
import { useAppearance, useDark } from './lib/appearance';
import { desktop, onDesktopCommand, requestSetOut } from './lib/desktop';
import { desktopState } from './lib/desktop-state';
import { request, workspace } from './lib/engine';
import { download, filename } from './lib/files';
import { goTo, isArea, notePath, type Page, pagePaths, taskPath, useRoute } from './lib/routes';
import { production } from './lib/runtime';

const pageTitles: Partial<Record<string, string>> = {
  Lab: 'Settings & recovery',
  Reviews: 'Weekly review',
  Task: 'Tasks / Task',
};

export default function App() {
  const mobileLayout = useMobileLayout();
  const state = useSyncExternalStore(workspace.subscribe, workspace.snapshot);
  const route = useRoute();
  const noteReturnPath = useRef<string | null>(null);

  useEffect(() => {
    if (route.page !== 'Note') {
      noteReturnPath.current = location.pathname;
    }
  }, [route.page]);

  const [selectedTask, setSelectedTask] = useState('');

  const [capture, setCapture] = useState(''),
    [search, setSearch] = useState('');

  const [switchTarget, setSwitchTarget] = useState<LocalRecord>(),
    [stoppingPoint, setStoppingPoint] = useState('');

  const [historyRows, setHistoryRows] = useState<RecordData[] | null>(null),
    [historyError, setHistoryError] = useState('');

  // Open while the owner compares versions; `id` picks the record to show first.
  const [conflictReview, setConflictReview] = useState<{ id?: string } | null>(null);

  // Task and session panels ask for the comparison without a prop chain.
  useEffect(() => {
    const open = (event: Event) =>
      setConflictReview({ id: (event as CustomEvent<string | undefined>).detail });

    window.addEventListener('cairn:conflict-review', open);

    return () => window.removeEventListener('cairn:conflict-review', open);
  }, []);

  const [appearance, setAppearance] = useAppearance();
  const dark = useDark();

  // The Mac app's palette, About box and updater (docs/desktop-app.md).
  const [paletteOpen, setPaletteOpen] = useState(false),
    [aboutOpen, setAboutOpen] = useState(false),
    [updateStatus, setUpdateStatus] = useState<UpdateStatus>();

  useEffect(() => {
    void workspace.boot();
  }, []);

  const notes = state.records.filter(r => r.kind === 'note' && !(r.value as Note).trashed),
    tasks = state.records.filter(r => r.kind === 'task' && !(r.value as Task).trashed);

  const routedNote = route.page === 'Note' ? notes.find(n => n.id === route.id) : undefined;

  const page =
    route.page === 'Note'
      ? ((routedNote?.value as Note | undefined)?.area ?? 'Not found')
      : route.page;

  useEffect(() => {
    setHistoryRows(null);
    setHistoryError('');
    setSearch('');
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    document.title = `${route.page === 'Task' ? ((tasks.find(t => t.id === route.id)?.value as Task | undefined)?.title ?? 'Task') : route.page === 'Note' ? ((routedNote?.value as Note | undefined)?.title ?? 'Note') : page === 'Lab' ? 'Settings & recovery' : page === 'Reviews' ? 'Weekly review' : page} · Cairn`;
  }, [page, route.page, route.id, tasks, routedNote]);

  const priorities = tasks.filter(
    r => (r.value as Task).today && !(r.value as Task).done && !(r.value as Task).dropped,
  );

  const _suggestions = taskSuggestions(state.records).filter(r => !(r.value as Task).today);

  const activeSession =
    state.records.find(
      r =>
        r.kind === 'focus' &&
        (r.value as FocusSession).deviceId === state.session?.deviceId &&
        (r.value as FocusSession).endedAt === null,
    ) ?? state.records.find(r => r.kind === 'focus' && (r.value as FocusSession).endedAt === null);

  const active =
    tasks.find(r => r.id === (activeSession?.value as FocusSession | undefined)?.taskId) ??
    tasks.find(
      r =>
        (r.value as Task).focus?.sessionOpen &&
        !state.records.some(s => s.kind === 'focus' && (s.value as FocusSession).taskId === r.id),
    );

  // Off the task page, only open work of the day is current: the chosen task
  // while it is still open, the running one, or the first open commitment. With
  // every commitment finished, or none chosen, there is no current task — as on
  // the phone.
  const currentTask =
    page === 'Task'
      ? tasks.find(r => r.id === route.id)
      : (priorities.find(r => r.id === selectedTask) ?? active ?? priorities[0]);

  const workingNote = notes.find(r => r.id === (currentTask?.value as Task | undefined)?.noteId);

  const matchingNotes = notes.filter(r => {
    const n = r.value as Note;

    return (
      n.area === page &&
      (!search || `${n.title} ${blockText(n.content)}`.toLowerCase().includes(search.toLowerCase()))
    );
  });

  const filteredNotes: LocalRecord[] = [];
  const visited = new Set<string>();

  function appendTree(record: LocalRecord) {
    if (visited.has(record.id)) {
      return;
    }

    visited.add(record.id);
    filteredNotes.push(record);
    matchingNotes.filter(child => (child.value as Note).parentId === record.id).forEach(appendTree);
  }

  matchingNotes
    .filter(record => !matchingNotes.some(parent => parent.id === (record.value as Note).parentId))
    .forEach(appendTree);

  matchingNotes.forEach(appendTree);

  const note =
    page === 'Task' ? workingNote : route.page === 'Note' ? routedNote : filteredNotes[0];

  const noteDraft = useRef<{ id: string; value: Note; incoming: string; dirty: boolean } | null>(
    null,
  );

  const noteEditor = useRef<Editor | null>(null);
  const noteTitle = useRef<HTMLInputElement>(null);

  if (
    note &&
    (noteDraft.current?.id !== note.id ||
      (!noteDraft.current.dirty && noteDraft.current.incoming !== JSON.stringify(note.value)))
  ) {
    noteDraft.current = {
      id: note.id,
      value: note.value as Note,
      incoming: JSON.stringify(note.value),
      dirty: false,
    };
  }

  function saveTask(record: LocalRecord, value: Task) {
    void workspace.save(record.id, 'task', value).catch(() => {});
  }

  function changeNote(patch: Partial<Note>) {
    if (!note || !noteDraft.current) {
      return;
    }

    if (
      noteDraft.current.value.locked &&
      !(Object.keys(patch).length === 1 && patch.locked === false)
    ) {
      return;
    }

    const value = { ...noteDraft.current.value, ...patch, updatedAt: new Date().toISOString() };

    noteDraft.current.value = value;
    noteDraft.current.dirty = true;
    const changes = [{ id: note.id, kind: 'note' as const, value }];

    if (patch.area) {
      const descendants = new Set([note.id]);
      let changed = true;

      while (changed) {
        changed = false;

        for (const row of state.records.filter(r => r.kind === 'note')) {
          const child = row.value as Note;

          if (child.parentId && descendants.has(child.parentId) && !descendants.has(row.id)) {
            descendants.add(row.id);
            changes.push({ id: row.id, kind: 'note', value: { ...child, area: patch.area } });
            changed = true;
          }
        }
      }
    }

    void workspace
      .saveMany(changes)
      .then(() => {
        if (
          noteDraft.current?.id === note.id &&
          JSON.stringify(noteDraft.current.value) === JSON.stringify(value)
        ) {
          noteDraft.current.dirty = false;
        }
      })
      .catch(() => {});
  }

  async function newNote(area: Area) {
    const id = crypto.randomUUID();

    await workspace.save(id, 'note', {
      title: '',
      area,
      parentId: null,
      content: { type: 'doc', content: [{ type: 'paragraph' }] },
      updatedAt: new Date().toISOString(),
    });

    goTo(notePath(id));
    setSearch('');
  }

  function navigate(next: Page) {
    goTo(pagePaths[next]);
  }

  async function captureTask(open = false) {
    if (!capture.trim()) {
      return;
    }

    const id = crypto.randomUUID();

    await workspace.save(id, 'task', {
      title: capture.trim(),
      noteId: null,
      done: false,
      today: false,
      steps: [],
      stoppingPoint: '',
    });

    setCapture('');

    if (open) {
      goTo(taskPath(id));
    }
  }

  function requestSwitch(record: LocalRecord) {
    setSwitchTarget(record);
    setStoppingPoint((active?.value as Task | undefined)?.stoppingDraft ?? '');
  }

  function changeStoppingPoint(value: string) {
    setStoppingPoint(value);
    const current = workspace.snapshot().records.find(r => r.id === active?.id);

    if (current) {
      saveTask(current, { ...(current.value as Task), stoppingDraft: value });
    }
  }

  async function switchSession() {
    if (!active || !switchTarget || !stoppingPoint.trim()) {
      return;
    }

    const endChanges = focusChanges(
      state.records,
      active.id,
      (activeSession?.value as FocusSession | undefined)?.deviceId ?? state.session!.deviceId,
      focusRuntime,
      'end',
      stoppingPoint,
    );

    const projected = [
      ...state.records.filter(r => !endChanges.some(c => c.id === r.id)),
      ...endChanges,
    ];

    await workspace.saveMany([
      ...endChanges,
      ...focusChanges(
        projected,
        switchTarget.id,
        state.session!.deviceId,
        focusRuntime,
        'start',
        '',
        Date.now(),
        !navigator.onLine || state.offline,
      ),
    ]);

    setSwitchTarget(undefined);
    setStoppingPoint('');
  }

  async function showHistory() {
    if (!note) {
      return;
    }

    setHistoryError('');

    try {
      const result = await request<RecordData[]>(`/api/history/${note.id}`);

      setHistoryRows(result);
    } catch (e) {
      setHistoryError((e as Error).message);
    }
  }

  function parentOptions() {
    if (!note) {
      return [];
    }

    const descendants = new Set([note.id]);
    let changed = true;

    while (changed) {
      changed = false;

      for (const n of notes) {
        if (
          (n.value as Note).parentId &&
          descendants.has((n.value as Note).parentId!) &&
          !descendants.has(n.id)
        ) {
          descendants.add(n.id);
          changed = true;
        }
      }
    }

    return notes.filter(
      n => !descendants.has(n.id) && (n.value as Note).area === (note.value as Note).area,
    );
  }

  // Inside the Mac app: tell it what the menu bar and the reminders show,
  // whenever the workspace changes and every few minutes as reminders come due.
  const signedIn = state.phase === 'ready' && Boolean(state.session);

  useEffect(() => {
    if (!desktop) {
      return;
    }

    const publish = () =>
      desktop?.publish(
        desktopState({
          signedIn,
          appearance,
          dark,
          records: state.records,
          deviceId: state.session?.deviceId,
        }),
      );

    const soon = setTimeout(publish, 250),
      every = setInterval(publish, 5 * 60 * 1000);

    return () => {
      clearTimeout(soon);
      clearInterval(every);
    };
  }, [signedIn, appearance, dark, state.records, state.session?.deviceId]);

  const commandHandlers = useLatest({ newNote, setAppearance });

  useEffect(
    () =>
      onDesktopCommand(command => {
        const handlers = commandHandlers.current;

        switch (command.type) {
          case 'navigate':
            goTo(command.path);
            break;
          case 'palette':
            setPaletteOpen(true);
            break;
          case 'about':
            setAboutOpen(true);
            break;
          case 'new-note':
            void handlers.newNote('Inbox');
            break;
          case 'set-out':
            goTo(pagePaths.Today);
            requestSetOut();
            break;
          case 'sync':
            void workspace.sync();
            break;
          case 'appearance':
            handlers.setAppearance(command.value);
            break;
          case 'full-screen':
            document.documentElement.dataset.fullScreen = String(command.value);
            break;
          case 'updates':
            setUpdateStatus(command.status);
            break;
          case 'capture': {
            const id = crypto.randomUUID();

            void workspace
              .save(id, 'task', {
                title: command.entry.title,
                noteId: null,
                done: false,
                today: command.entry.today,
                steps: [],
                stoppingPoint: '',
              })
              .then(() => {
                desktop?.reply({ id: command.id, ok: true });

                if (command.entry.open) {
                  goTo(taskPath(id));
                }
              })
              .catch(() =>
                desktop?.reply({
                  id: command.id,
                  ok: false,
                  error: 'Cairn could not save the task. Try again.',
                }),
              );

            break;
          }
        }
      }),
    [commandHandlers],
  );

  useEffect(() => {
    void desktop?.updates('status').then(setUpdateStatus);
  }, []);

  if (state.phase === 'loading') {
    return (
      <div className="loading">
        {desktop && <div className="window-drag" />}
        <Mountain size={32} />
        <p>Opening your workbench…</p>
      </div>
    );
  }

  if (state.phase === 'locked') {
    return (
      <>
        {desktop && <div className="window-drag" />}
        <AuthGate notice={state.error || state.notice} />
      </>
    );
  }

  const status = state.error
    ? 'Needs attention'
    : state.conflicts
      ? `${state.conflicts} to review`
      : state.saving
        ? 'Saving locally…'
        : state.paused
          ? `${state.pending} pending · sync paused`
          : state.offline
            ? `${state.pending} pending · offline`
            : state.syncing
              ? 'Syncing…'
              : state.pending
                ? `${state.pending} pending`
                : 'Synced';

  const editor = note ? (
    <section className="working-note" data-area={(note.value as Note).area} key={note.id}>
      <div className="note-path">
        <span>
          <FileText size={13} />
          {(note.value as Note).area} / Working note
        </span>
        <div className="button-row">
          <IconButton
            label="Undo"
            title="Undo (⌘Z)"
            disabled={(note.value as Note).locked}
            onClick={() => noteEditor.current?.chain().focus().undo().run()}
          >
            <Undo2 size={15} />
          </IconButton>
          <IconButton
            label="Redo"
            title="Redo (⌘⇧Z)"
            disabled={(note.value as Note).locked}
            onClick={() => noteEditor.current?.chain().focus().redo().run()}
          >
            <Redo2 size={15} />
          </IconButton>
          <IconButton
            label={(note.value as Note).locked ? 'Unlock note' : 'Lock note'}
            onClick={() => changeNote({ locked: !(note.value as Note).locked })}
          >
            {(note.value as Note).locked ? (
              <LockKeyhole aria-hidden="true" />
            ) : (
              <LockKeyholeOpen aria-hidden="true" />
            )}
          </IconButton>
          <IconButton
            title="Revision history"
            label="Revision history"
            onClick={() => void showHistory()}
          >
            <History size={15} />
          </IconButton>
          <IconButton
            label="Export note as Markdown"
            title="Export Markdown"
            onClick={() =>
              download(
                `${filename((note.value as Note).title)}.md`,
                `# ${(note.value as Note).title}\n\n${blockText(resolveImages((note.value as Note).content, state.records), true)}`,
                'text/markdown',
              )
            }
          >
            <Download size={15} />
          </IconButton>
          <ActionIconButton
            disabled={(note.value as Note).locked}
            action="trash"
            label="Move note to trash"
            onClick={() => changeNote({ trashed: true })}
          />
        </div>
      </div>
      <div className="note-title-wrap">
        <div className="note-heading">
          <NoteIconPicker
            disabled={(note.value as Note).locked}
            icon={noteDraft.current?.value.icon ?? null}
            tone={noteDraft.current?.value.iconTone ?? null}
            onChange={(icon, iconTone) => changeNote({ icon, iconTone })}
          />
          <Input
            disabled={(note.value as Note).locked}
            variant="inline"
            className="note-title"
            aria-label="Note title"
            placeholder="Untitled"
            ref={noteTitle}
            value={noteDraft.current?.value.title ?? (note.value as Note).title}
            onChange={e => changeNote({ title: e.target.value })}
            onKeyDown={e => {
              // Return and Down leave the title for the page, as in Notion.
              if (e.key === 'Enter' || e.key === 'ArrowDown') {
                e.preventDefault();
                noteEditor.current?.chain().focus('start').run();
              }
            }}
          />
        </div>
        <div className="note-properties">
          <Select
            disabled={(note.value as Note).locked}
            aria-label="Note area"
            value={(note.value as Note).area}
            onChange={e => {
              const area = e.target.value as Area;

              changeNote({ area, parentId: null });

              if (page !== 'Today' && page !== 'Task') {
                goTo(notePath(note.id));
              }
            }}
          >
            {areas.map(a => (
              <option key={a}>{a}</option>
            ))}
          </Select>
          <Select
            disabled={(note.value as Note).locked}
            aria-label="Parent page"
            value={(note.value as Note).parentId ?? ''}
            onChange={e => changeNote({ parentId: e.target.value || null })}
          >
            <option value="">Top-level page</option>
            {parentOptions().map(n => (
              <option value={n.id} key={n.id}>
                {(n.value as Note).title || 'Untitled'}
              </option>
            ))}
          </Select>
          <span>{note.operationId ? 'Saved on device' : `Revision ${note.revision}`}</span>
        </div>
      </div>
      {note.competing ? (
        <div className="notice conflict-notice" role="status">
          <p>
            <strong>This note was also changed on another device.</strong> Your edits are saved here
            and wait until you choose which version to keep.
          </p>
          <Button variant="secondary" onClick={() => setConflictReview({ id: note.id })}>
            Compare versions
          </Button>
        </div>
      ) : (
        note.conflict && (
          <div className="notice conflict-notice replaced" role="status">
            <p>
              Another device saved this note at the same time, and an older Cairn replaced its
              version with this one. The replaced version is in revision history.
            </p>
            <Button variant="secondary" onClick={() => void showHistory()}>
              Open revision history
            </Button>
          </div>
        )
      )}
      {historyError && <p className="error-message">{historyError}</p>}
      {historyRows !== null && (
        <div className="history-panel">
          <div className="section-heading">
            <strong>Earlier server revisions</strong>
            <IconButton label="Close revision history" onClick={() => setHistoryRows(null)}>
              <X size={14} />
            </IconButton>
          </div>
          {historyRows.length ? (
            historyRows.map(row => (
              <Disclosure
                key={row.revision}
                summary={`Revision ${row.revision} · ${new Date(row.updatedAt).toLocaleString()}`}
              >
                <pre>{blockText((row.value as Note).content)}</pre>
                <ActionIconButton
                  disabled={(note.value as Note).locked}
                  action="restore"
                  label="Restore as a new revision"
                  onClick={() => {
                    changeNote(row.value as Note);
                    setHistoryRows(null);
                  }}
                />
              </Disclosure>
            ))
          ) : (
            <p>No earlier synced revisions yet.</p>
          )}
        </div>
      )}
      <NoteEditor
        locked={(note.value as Note).locked}
        noteId={note.id}
        content={(note.value as Note).content}
        onChange={content => changeNote({ content })}
        editorRef={noteEditor}
        onExitTop={() => {
          const input = noteTitle.current;

          input?.focus();
          input?.setSelectionRange(input.value.length, input.value.length);
        }}
      />
    </section>
  ) : (
    <div className="empty-state">
      <BookOpen size={32} />
      <h2>{page === 'Task' ? 'Keep your context close.' : 'A little room for your thoughts.'}</h2>
      <p>
        {page === 'Task'
          ? 'Link a note from the task panel, or create one in your library.'
          : 'Create a page or change your search.'}
      </p>
      <ActionIconButton
        action="note"
        label="Create a note"
        onClick={() => void newNote(isArea(page) ? page : 'Inbox')}
      />
    </div>
  );

  const workingNoteView =
    mobileLayout && note ? (
      <section className="working-note-shortcut">
        <ButtonLink href={notePath(note.id)}>
          Open working note: {(note.value as Note).title || 'Untitled'}
        </ButtonLink>
      </section>
    ) : (
      editor
    );

  const libraryNoteView = (
    <MobileDetails
      open={route.page === 'Note' && Boolean(note)}
      title="Note"
      closeLabel="Close note"
      onClose={() =>
        goTo(
          noteReturnPath.current ?? pagePaths[(note?.value as Note | undefined)?.area ?? 'Inbox'],
        )
      }
      busy={Boolean(state.saving)}
      status={state.error || state.notice || status}
    >
      {editor}
    </MobileDetails>
  );

  const taskControls = currentTask ? (
    <div className="task-controls">
      <TaskContext
        key={`context:${currentTask.id}`}
        record={currentTask}
        records={state.records}
        expanded={page === 'Task'}
      />
      <div className="button-row">
        <Button
          variant="ghost"
          onClick={() =>
            saveTask(currentTask, {
              ...(currentTask.value as Task),
              today: !(currentTask.value as Task).today,
            })
          }
        >
          {(currentTask.value as Task).today ? 'Move back to Inbox' : 'Choose for Today'}
        </Button>
      </div>
      <FocusPanel
        key={`focus:${currentTask.id}`}
        record={currentTask}
        records={state.records}
        deviceId={state.session!.deviceId}
        notes={notes}
        active={active}
        save={value => saveTask(currentTask, value)}
        startOther={() => requestSwitch(currentTask)}
      />
    </div>
  ) : null;

  return (
    <div className="app-shell" data-section={page}>
      <Sidebar
        page={page}
        today={priorities.length}
        notes={notes}
        dark={dark}
        onToggleTheme={() => setAppearance(dark ? 'light' : 'dark')}
        onSignOut={() => void workspace.signOut()}
      />
      <div className="app-body">
        <header className="topbar">
          <div>
            {desktop && <HistoryButtons />}
            <span className="breadcrumb">
              Personal <span>/</span> <strong>{pageTitles[page] ?? page}</strong>
            </span>
          </div>
          {desktop && (
            <CommandCenter
              title={
                route.page === 'Task'
                  ? ((currentTask?.value as Task | undefined)?.title ?? 'Task')
                  : route.page === 'Note'
                    ? (routedNote?.value as Note | undefined)?.title || 'Untitled'
                    : (pageTitles[page] ?? page)
              }
              onOpen={() => setPaletteOpen(true)}
            />
          )}
          <button
            type="button"
            data-ui="specialized"
            className={`sync-status tone-${state.error || state.conflicts ? 'danger' : state.offline || state.paused || state.pending ? 'attention' : state.saving || state.syncing ? 'info' : 'success'}`}
            title={
              state.conflicts && !state.error
                ? 'Compare the versions waiting for your choice'
                : 'Open storage status'
            }
            onClick={() =>
              state.conflicts && !state.error ? setConflictReview({}) : navigate('Lab')
            }
          >
            {state.conflicts && !state.error ? (
              <GitCompareArrows size={14} />
            ) : state.offline ? (
              <CloudOff size={14} />
            ) : (
              <Cloud size={14} />
            )}
            <span>{status}</span>
          </button>
        </header>
        {(state.error || state.notice) && (
          <div
            className={state.error ? 'global-notice error-message' : 'global-notice notice'}
            role={state.error ? 'alert' : 'status'}
          >
            {state.error || state.notice}
            <IconButton label="Dismiss message" onClick={() => workspace.clearNotice()}>
              <X size={14} />
            </IconButton>
          </div>
        )}
        <ConflictBanner records={state.records} onReview={() => setConflictReview({})} />
        {conflictReview && (
          <ConflictReview
            records={state.records}
            initial={conflictReview.id}
            onClose={() => setConflictReview(null)}
          />
        )}
        <FocusActivity records={state.records} deviceId={state.session!.deviceId} />
        {page !== 'Calendar' && (
          <CalendarReminders records={state.records} open={() => navigate('Calendar')} />
        )}
        {page === 'Task' ? (
          <TaskPage
            record={currentTask}
            records={state.records}
            controls={taskControls}
            note={workingNoteView}
          />
        ) : page === 'Not found' ? (
          <main className="productivity-page">
            <PageHeader
              title="Page not found"
              description="This address does not match an available page."
            />
            <ButtonLink variant="secondary" href={pagePaths.Today}>
              Go to Today
            </ButtonLink>
          </main>
        ) : page === 'Today' ? (
          <TodayPage
            records={state.records}
            notes={notes}
            priorities={priorities}
            currentTask={currentTask}
            active={active}
            deviceId={state.session!.deviceId}
            capture={capture}
            onCapture={setCapture}
            onCaptureSubmit={() => void captureTask()}
            onSelect={setSelectedTask}
            onSaveTask={saveTask}
            onSwitch={requestSwitch}
            name={state.session?.name ?? ''}
          />
        ) : page === 'Notes' ? (
          <NotesPage notes={notes} />
        ) : page === 'More' ? (
          <MorePage
            appearance={appearance}
            onAppearance={setAppearance}
            onSync={() => void workspace.sync()}
            onSignOut={() => void workspace.signOut()}
            records={state.records}
            pending={state.pending}
            lastSync={state.lastSync}
            email={state.session?.email}
          />
        ) : page === 'Learning' ? (
          <LearningPage records={state.records} offline={state.offline} />
        ) : page === 'Calendar' ? (
          <CalendarPage
            records={state.records}
            openTask={id => goTo(taskPath(id))}
            openNote={id => goTo(notePath(id))}
          />
        ) : page === 'Tasks' ? (
          <main className="productivity-page">
            <PageHeader
              title="All tasks"
              description="Every commitment, with its next step in view."
            />
            <InlineCreate
              label="New task title"
              placeholder="What needs doing?"
              value={capture}
              onChange={setCapture}
              action="Create task"
              onSubmit={e => {
                e.preventDefault();
                void captureTask(true);
              }}
            />
            <TasksTable
              records={state.records}
              save={saveTask}
              deviceId={state.session!.deviceId}
            />
          </main>
        ) : page === 'Trash' ? (
          <WorkspaceTrash records={state.records} />
        ) : page === 'Goals' ? (
          <GoalsPage records={state.records} />
        ) : page === 'Reviews' ? (
          <ReviewsPage records={state.records} openNotes={() => navigate('Inbox')} />
        ) : page === 'People' ? (
          <PeoplePage records={state.records} />
        ) : page === 'Vault' ? (
          <VaultLab
            records={state.records.filter(r => r.kind === 'vault' && !r.deleted)}
            save={(id, value) => workspace.save(id, 'vault', value)}
            reviewConflict={id => setConflictReview({ id })}
          />
        ) : page === 'Lab' ? (
          <main className="lab-page">
            <PageHeader
              eyebrow="WORKSPACE HEALTH"
              title="Settings & recovery"
              description="Manage storage, synchronization, backups and exports."
            />
            <div className="lab-warning">
              <Shield size={18} />
              <p>
                {production
                  ? 'Your workspace is deployed. Vault security review, device checks and backup recovery qualification remain open. Use test credentials in the vault until those checks are complete.'
                  : 'This local preview uses test accounts. Vault is part of this workspace; use test credentials while validating security and recovery.'}
              </p>
            </div>
            <dl className="status-grid">
              <div>
                <dt>Device storage</dt>
                <dd>IndexedDB · encrypted records</dd>
              </div>
              <div>
                <dt>Synchronization</dt>
                <dd>{status}</dd>
              </div>
              <div>
                <dt>Last successful sync</dt>
                <dd>
                  {state.lastSync ? new Date(state.lastSync).toLocaleString() : 'Not this session'}
                </dd>
              </div>
              <div>
                <dt>Session expires</dt>
                <dd>{state.session && new Date(state.session.expiresAt).toLocaleString()}</dd>
              </div>
              <div>
                <dt>Local records</dt>
                <dd>{state.records.length}</dd>
              </div>
              <div>
                <dt>Browser persistence</dt>
                <dd>
                  {state.persistent === undefined
                    ? 'Not checked'
                    : state.persistent
                      ? 'Granted'
                      : 'Not granted'}
                </dd>
              </div>
            </dl>
            <div className="button-row wrap storage-actions">
              <Button
                variant="primary"
                onClick={() => void workspace.sync()}
                disabled={state.paused || state.syncing}
              >
                Synchronize now
              </Button>
              <Button variant="secondary" onClick={() => workspace.pauseSync(!state.paused)}>
                {state.paused ? 'Resume sync' : 'Pause sync for testing'}
              </Button>
              <Button variant="secondary" onClick={() => void workspace.requestPersistence()}>
                Request persistent storage
              </Button>
            </div>
            <section className="detail-card">
              <h2>Appearance</h2>
              <AppearancePicker appearance={appearance} onChange={setAppearance} />
              <p className="small-muted">Kept on this device. The phone keeps its own.</p>
            </section>
            <BackupStatus />
            {desktop ? <DesktopReminders /> : <ReminderDelivery />}
            {!desktop && <DesktopDownload />}
            <WorkspaceSettings />
            <WorkspaceTransfer records={state.records} />
            <section className="detail-card lab-instructions">
              <h2>Try the offline loop</h2>
              <ol>
                <li>Pause sync, edit a note, and check “Saved on device.”</li>
                <li>Reload this page. Your encrypted edit should still be here.</li>
                <li>Resume sync and check the pending count returns to zero.</li>
                <li>Open a note’s history to inspect earlier server versions.</li>
              </ol>
              <p>
                Pausing sync tests queued writes. A true offline reload also requires loading the
                production build once so its service worker can cache the app shell. Signing out
                removes the local decryption key; encrypted pending edits remain until online
                sign-in.
              </p>
              <p>
                The server is trusted to decrypt your workspace and vault.{' '}
                {production
                  ? 'Account recovery is sent to your email address and resets both your password and authenticator. Keep that email account protected.'
                  : 'The local test mailbox exposes recovery links for fictitious accounts. Keep this preview on localhost.'}
              </p>
            </section>
          </main>
        ) : (
          <div className="library-layout">
            <section className="note-list">
              <PageHeader
                compact
                title={page}
                actions={
                  <ActionIconButton
                    action="note"
                    label="New note"
                    onClick={() => void newNote(page)}
                  />
                }
              />
              <SearchField label="Search notes" value={search} onChange={setSearch} />
              <NoteList
                records={filteredNotes}
                notes={notes}
                selectedId={note?.id}
                searching={Boolean(search)}
              />
            </section>
            {libraryNoteView}
          </div>
        )}
      </div>
      <TabBar page={route.page === 'Task' ? 'Task' : page} today={priorities.length} />
      {switchTarget && (
        <Modal
          open
          onClose={() => setSwitchTarget(undefined)}
          title="Leave a marker"
          onSubmit={e => {
            e.preventDefault();
            void switchSession();
          }}
        >
          <label>
            <span className="sr-only">Where are you leaving it?</span>
            <Textarea
              autoFocus
              required
              className="marker-field"
              value={stoppingPoint}
              onChange={e => changeStoppingPoint(e.target.value)}
              placeholder="Where are you leaving it?"
            />
          </label>
          <MarkerStarts value={stoppingPoint} onChange={changeStoppingPoint} />
          <p>
            A sentence is enough. It is what you will see when you come back to “
            {(active?.value as Task)?.title}”.
          </p>
          <div className="button-row">
            <Button variant="primary">Leave marker & switch</Button>
            <Button variant="secondary" type="button" onClick={() => setSwitchTarget(undefined)}>
              Keep working
            </Button>
          </div>
        </Modal>
      )}
      {desktop && (
        <>
          <GoToPalette
            open={paletteOpen}
            onClose={() => setPaletteOpen(false)}
            records={state.records}
            dark={dark}
            onAppearance={setAppearance}
            onNewNote={() => void newNote('Inbox')}
            onSetOut={() => {
              goTo(pagePaths.Today);
              requestSetOut();
            }}
            onAbout={() => setAboutOpen(true)}
          />
          <AboutCairn
            open={aboutOpen}
            onClose={() => setAboutOpen(false)}
            email={state.session?.email}
            status={updateStatus}
          />
          <DesktopUpdateNotice status={updateStatus} />
        </>
      )}
    </div>
  );
}
